import type { OrderBy } from '@/lib/graphql-query-builder';

/**
 * MbmProject is the catalog of customer projects that timesheet entries and annual reviews point
 * at. The legacy backend fed it from the external MBM system and treated a project as active
 * only while `Status === 'Active'`; any other status string coming from MBM means "not active".
 */
export const MBM_ACTIVE_STATUS = 'Active';
export const MBM_INACTIVE_STATUS = 'Inactive';

export interface MbmProject {
  ItemId: string;
  ProjectId: string;
  ProjectName: string;
  CustomerId: string;
  CustomerName: string;
  Status?: string;
  /** JSON-serialized `MbmProjectHistoryEntry[]`; use `parseMbmProjectHistory` to read it. */
  MbmProjectHistory?: string;
  CreatedDate?: string;
  LastUpdatedDate?: string;
  LastUpdatedBy?: string;
}

/** One snapshot of the project, appended every time it is created or changed. */
export interface MbmProjectHistoryEntry {
  ProjectId: string;
  ProjectName: string;
  CustomerId: string;
  CustomerName: string;
  Status: string;
  CreateDate: string;
}

export interface MbmProjectsPagingResult {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  totalPages: number;
  pageSize: number;
  pageNo: number;
  items: MbmProject[];
}

export type MbmProjectStatusFilter = 'all' | 'active' | 'inactive';

export interface MbmProjectFilters {
  search?: string;
  customerId?: string;
  status?: MbmProjectStatusFilter;
}

export interface GetMbmProjectsParams extends MbmProjectFilters {
  pageNo: number;
  pageSize: number;
  /** Column filters, combined with the search, customer and status filters. */
  filter?: Record<string, unknown>;
  /** Defaults to the latest change first. */
  order?: OrderBy[];
}

export interface MbmCustomer {
  CustomerId: string;
  CustomerName: string;
}

export interface MbmCustomersResult {
  totalCount: number;
  items: MbmCustomer[];
}

export interface MbmProjectInsertInput {
  ProjectId: string;
  ProjectName: string;
  CustomerId: string;
  CustomerName: string;
  Status: string;
  MbmProjectHistory: string;
}

export type MbmProjectUpdateInput = Partial<
  Pick<MbmProjectInsertInput, 'ProjectName' | 'CustomerName' | 'Status' | 'MbmProjectHistory'>
>;
